import { ChangeEvent, useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { Helmet } from "react-helmet-async";
import * as Yup from "yup";
import { MultiValue, SingleValue } from "react-select";
import { AppDispatch, AppState } from "../../../redux/store";
import PermissionGate from "../../../core/layouts/permission-gate";
import ToastComponent from "../../../components/toast";
import StyledSelect from "../../../components/styled-select";
import { OptionValue } from "../../../types/component";
import Input from "../../../components/input";
import Button from "../../../components/button";
import { AddOutlined } from "../../../components/icons/add-outlined";
import { MinusOutlined } from "../../../components/icons/minus-outlined";
import { Formik, FormikHelpers } from "formik";
import {
  clearUpdateCourseState,
  courseByIdStateAction,
  getCourseByIdState,
  getUpdateCourseState,
  updateCourseStateAction,
  CourseStatus,
  CourseLanguage,
  CourseLevel,
  CreateAttendance,
  markAttendanceStateAction,
  unmarkAttendanceStateAction,
  attendanceByCourseStateAction,
  clearMarkAttendanceState,
  clearUnmarkAttendanceState,
  getAttendanceByCourseState,
  getMarkAttendanceState,
  getUnmarkAttendanceState,
} from "../../../redux/features/course-slice";
import { UserRole } from "../../../redux/features/user-slice";
import {
  getErrorState,
  setBreadcrumbState,
  setErrorState,
} from "../../../redux/features/common-slice";
import {
  allIncidentUsersStateAction,
  getAllIncidentUsersState,
} from "../../../redux/features/incident-slice";
import paths from "../../../navigation/paths";
import { adminCat } from "../../../core/utils/constants";
import { isSelectSingleValue, isSelectMultiValue } from "../../../core/utils";
import { addWeeks, format } from "date-fns";
import DatePicker from "../../../components/date-picker";
import TimePicker from "../../../components/time-picker";
import CheckBox from "../../../components/checkbox";
import { ErrorState } from "../../../redux/common-state";
import { getUserState } from "../../../redux/features/auth-slice";
import InputFormat from "../../../components/input-format";

interface CourseUpdateFormValues {
  name: string;
  description: string;
  syllabus: string;
  prerequisite: string;
  duration: number;
  schedule: string[];
  capacity: number;
  status: CourseStatus;
  language: CourseLanguage;
  level: CourseLevel;
  authorIds: string[];
  picture?: File | null;
}

interface LocalAttendance {
  userId: string;
  date: string;
  attended: boolean;
  attendanceId?: string;
}

export default function UpdateCoursePage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const dispatch = useDispatch<AppDispatch>();
  const courseState = useSelector(getCourseByIdState);
  const updateCourseState = useSelector(getUpdateCourseState);
  const markAttendanceState = useSelector(getMarkAttendanceState);
  const unmarkAttendanceState = useSelector(getUnmarkAttendanceState);
  const errorState = useSelector(getErrorState);
  const usersState = useSelector(getAllIncidentUsersState);
  const attendanceState = useSelector(getAttendanceByCourseState);
  const userState = useSelector(getUserState);
  const [newScheduleDate, setNewScheduleDate] = useState<Date | null>(null);
  const [newScheduleTime, setNewScheduleTime] = useState<string>("");
  const [localAttendance, setLocalAttendance] = useState<LocalAttendance[]>([]);

  useEffect(() => {
    if (id) {
      dispatch(attendanceByCourseStateAction({ courseId: id }));
    }
  }, [dispatch, id]);

  const handleSuccessToast = useCallback(
    (message: string, clearAction: () => void) => {
      toast(message, { type: "success" });
      setTimeout(() => {
        dispatch(clearUpdateCourseState());
        dispatch(clearMarkAttendanceState());
        dispatch(clearUnmarkAttendanceState());
      });
    },
    [dispatch]
  );

  const handleLocalAttendanceChange = useCallback(
    (userId: string, date: string, attended: boolean) => {
      setLocalAttendance((prev) => {
        const existingAttendance = attendanceState.data.find(
          (att) => att.user.id === userId && att.date === date
        );
        const existing = prev.find(
          (att) => att.userId === userId && att.date === date
        );
        if (existing) {
          return prev.map((att) =>
            att.userId === userId && att.date === date
              ? { ...att, attended, attendanceId: existingAttendance?.id }
              : att
          );
        }
        return [
          ...prev,
          {
            userId,
            date,
            attended,
            attendanceId: existingAttendance?.id,
          },
        ];
      });
    },
    [attendanceState.data]
  );

  const handleSubmitAttendance = useCallback(async () => {
    if (!id || localAttendance.length === 0) {
      toast("No attendance changes to submit", { type: "warning" });
      return;
    }
    try {
      console.log("Submitting attendance, localAttendance:", localAttendance);

      // Filter for new attendances to mark (attended: true, no attendanceId)
      const toMark: CreateAttendance[] = localAttendance
        .filter((att) => att.attended && !att.attendanceId)
        .map((att) => ({
          courseId: id,
          userId: att.userId,
          date: att.date,
          attended: att.attended,
        }));

      // Filter for existing attendances to unmark (attended: false, has attendanceId)
      const toUnmark: string[] = localAttendance
        .filter((att) => !att.attended && att.attendanceId)
        .map((att) => att.attendanceId!);

      let successMessage = "";
      let hasChanges = false;

      // Handle marking new attendances
      if (toMark.length > 0) {
        const markResult = await dispatch(
          markAttendanceStateAction({ request: { attendances: toMark } })
        );
        if (markAttendanceStateAction.fulfilled.match(markResult)) {
          successMessage += "Attendances marked successfully. ";
          hasChanges = true;
        } else {
          const error = markResult.payload as ErrorState;
          throw new Error(error?.message || "Failed to mark attendances");
        }
      }

      // Handle unmarking existing attendances
      if (toUnmark.length > 0) {
        for (const attendanceId of toUnmark) {
          const unmarkResult = await dispatch(
            unmarkAttendanceStateAction({ attendanceId })
          );
          if (unmarkAttendanceStateAction.fulfilled.match(unmarkResult)) {
            successMessage += "Attendances unmarked successfully. ";
            hasChanges = true;
          } else {
            const error = unmarkResult.payload as ErrorState;
            throw new Error(
              error?.message || `Failed to unmark attendance ${attendanceId}`
            );
          }
        }
      }

      if (hasChanges) {
        handleSuccessToast(successMessage.trim(), () => {
          dispatch(clearMarkAttendanceState());
          dispatch(clearUnmarkAttendanceState());
        });
        setLocalAttendance([]);
        await dispatch(attendanceByCourseStateAction({ courseId: id }));
      } else {
        toast("No changes were applied", { type: "info" });
      }
    } catch (error: any) {
      console.error("handleSubmitAttendance error:", error);
      toast(error.message || "Failed to update attendances", { type: "error" });
    }
  }, [dispatch, id, localAttendance, handleSuccessToast]);

  const fetchCourseDetail = useCallback(async () => {
    if (id) {
      await dispatch(courseByIdStateAction({ id }));
    }
  }, [dispatch, id]);

  const fetchAllUsers = useCallback(async () => {
    await dispatch(allIncidentUsersStateAction({ teamIds: [], roles: [] }));
  }, [dispatch]);

  const submitUpdateCourse = async (
    values: CourseUpdateFormValues,
    { setSubmitting }: FormikHelpers<CourseUpdateFormValues>
  ) => {
    if (id) {
      const formData = new FormData();
      if (values.name) formData.append("name", values.name);
      if (values.description)
        formData.append("description", values.description);
      if (values.syllabus) formData.append("syllabus", values.syllabus);
      if (values.prerequisite)
        formData.append("prerequisite", values.prerequisite);
      if (values.duration !== undefined)
        formData.append("duration", values.duration.toString());
      if (values.schedule) {
        values.schedule.forEach((date, index) => {
          formData.append(`schedule[${index}]`, date);
        });
      }
      if (values.capacity !== undefined)
        formData.append("capacity", values.capacity.toString());
      if (values.status) formData.append("status", values.status);
      if (values.language) formData.append("language", values.language);
      if (values.level) formData.append("level", values.level);
      if (values.authorIds) {
        values.authorIds.forEach((id, index) => {
          formData.append(`authorIds[${index}]`, id);
        });
      }
      if (values.picture) {
        formData.append("picture", values.picture);
      }

      await dispatch(
        updateCourseStateAction({
          id,
          request: formData,
          userId: "current-user-id", // TODO: Replace with actual user ID
        })
      );
      setSubmitting(false);
    }
  };

  const handleFileChange = (
    event: ChangeEvent<HTMLInputElement>,
    setFieldValue: (field: string, value: any) => void
  ) => {
    const file = event.target.files?.[0];
    if (file && (file.type === "image/jpeg" || file.type === "image/png")) {
      if (file.size > 5 * 1024 * 1024) {
        toast("File size exceeds 5MB limit", { type: "error" });
        setFieldValue("picture", null);
      } else {
        setFieldValue("picture", file);
      }
    } else {
      toast("Please select a valid JPEG or PNG image", { type: "error" });
      setFieldValue("picture", null);
    }
  };

  useEffect(() => {
    fetchCourseDetail();
    fetchAllUsers();
  }, [fetchCourseDetail, fetchAllUsers]);

  useEffect(() => {
    if (courseState.data) {
      dispatch(
        setBreadcrumbState([
          { id: paths.COURSES, path: null, title: "Courses" },
          { id: courseState.data.id, path: null, title: courseState.data.name },
          { id: "updateCourse", path: null, title: "Update Course" },
        ])
      );
    }
  }, [courseState.data, dispatch]);

  useEffect(() => {
    if (updateCourseState.data && updateCourseState.data.success) {
      handleSuccessToast(
        updateCourseState.data.message,
        clearUpdateCourseState
      );
      // setTimeout(() => {
      //   window.location.reload();
      // }, 2000);
    }
  }, [updateCourseState, handleSuccessToast]);

  useEffect(() => {
    if (errorState) {
      toast(errorState.message, { type: "error" });
      dispatch(setErrorState(null));
    }
  }, [errorState, dispatch]);

  const getDefaultScheduleDate = (schedule: string[]): Date => {
    if (schedule.length === 0) return addWeeks(new Date(), 1);
    const lastDate = new Date(schedule[schedule.length - 1]);
    return addWeeks(lastDate, 1);
  };

  // Check if user is authorized to view/edit the course
  const isAdmin =
    userState?.data?.role === UserRole.Admin ||
    userState?.data?.role === UserRole.Trainer;
  const isAuthor = courseState.data?.authors.some(
    (author) => author.id === userState?.data?.id
  );
  const isAuthorized = isAdmin || isAuthor;

  // Redirect if unauthorized
  useEffect(() => {
    if (courseState.data && userState?.data && !isAuthorized) {
      toast("You are not authorized to edit this course", { type: "error" });
      navigate(paths.COURSES, { replace: true });
    }
  }, [courseState.data, userState, isAuthorized, navigate]);

  return (
    <PermissionGate categories={[]} roles={[]}>
      {courseState.data && (
        <>
          <Helmet title={`Update ${courseState.data.name}`} />
          <Formik
            initialValues={
              {
                name: courseState.data?.name || "",
                description: courseState.data?.description || "",
                prerequisite: courseState.data?.prerequisite || "",
                syllabus: courseState.data?.syllabus || "",
                duration: courseState.data?.duration || 0,
                schedule: courseState.data?.schedule || [],
                capacity: courseState.data?.capacity || 10,
                status: courseState.data?.status || CourseStatus.Draft,
                language:
                  courseState.data?.language || CourseLanguage.Vietnamese,
                level: courseState.data?.level || CourseLevel.Beginner,
                authorIds:
                  courseState.data?.authors.map((author) => author.id) || [],
                picture: null,
              } as CourseUpdateFormValues
            }
            validationSchema={Yup.object().shape({
              name: Yup.string().required("Name is required").max(100),
              description: Yup.string().required("Description is required"),
              syllabus: Yup.string().required("Syllabus is required"),
              duration: Yup.number().required("Duration is required").min(1),
              schedule: Yup.array()
                .of(Yup.string().required())
                .min(1, "At least one schedule date is required"),
              capacity: Yup.number().required("Capacity is required").min(1),
              status: Yup.string().required("Status is required"),
              language: Yup.string().required("Language is required"),
              level: Yup.string().required("Level is required"),
              authorIds: Yup.array()
                .of(Yup.string().required())
                .min(1, "At least one author is required"),
              picture: Yup.mixed().nullable(),
            })}
            onSubmit={submitUpdateCourse}
          >
            {({
              values,
              errors,
              handleChange,
              handleSubmit,
              isSubmitting,
              setValues,
              setFieldValue,
              dirty,
            }) => (
              <form
                noValidate
                autoComplete="off"
                onSubmit={handleSubmit}
                className="h-full overflow-y-auto"
                encType="multipart/form-data"
              >
                <div
                  className="h-full grid grid-cols-1 sm:grid-cols-2 gap-8 overflow-y-scroll"
                  style={{
                    height: "calc(100% - 56px)",
                  }}
                >
                  <div className="space-y-4">
                    <Input
                      label="Name *"
                      type="text"
                      id="name"
                      value={values.name}
                      onChange={handleChange}
                      errorText={errors.name}
                    />
                    <InputFormat
                      label="Description *"
                      id="description-editor"
                      height="150px"
                      value={values.description}
                      onChange={(value) =>
                        setValues({ ...values, description: value })
                      }
                      errorText={errors.description}
                    />
                    <InputFormat
                      label="Syllabus *"
                      id="syllabus-editor"
                      value={values.syllabus}
                      onChange={(value) =>
                        setValues({ ...values, syllabus: value })
                      }
                      errorText={errors.syllabus}
                    />
                    <Input
                      label="Prerequisite"
                      type="text"
                      id="prerequisite"
                      value={values.prerequisite}
                      onChange={handleChange}
                      errorText={errors.prerequisite}
                    />
                    <Input
                      label="Duration (hours) *"
                      type="number"
                      id="duration"
                      value={values.duration.toString()}
                      onChange={handleChange}
                      errorText={errors.duration}
                    />
                    <Input
                      label="Capacity *"
                      type="number"
                      id="capacity"
                      value={values.capacity.toString()}
                      onChange={handleChange}
                      errorText={errors.capacity}
                    />
                    <div className="flex flex-col">
                      <label className="text-sm text-text-title">
                        Status *
                      </label>
                      <StyledSelect
                        options={Object.values(CourseStatus).map((status) => ({
                          value: status,
                          label:
                            status.charAt(0).toUpperCase() + status.slice(1),
                        }))}
                        isClearable={false}
                        menuPosition="fixed"
                        value={{
                          value: values.status,
                          label:
                            values.status.charAt(0).toUpperCase() +
                            values.status.slice(1),
                        }}
                        onChange={(
                          value:
                            | SingleValue<OptionValue>
                            | MultiValue<OptionValue>
                        ) => {
                          if (value && isSelectSingleValue(value)) {
                            setValues({
                              ...values,
                              status: value.value as CourseStatus,
                            });
                          }
                        }}
                      />
                      {errors.status && (
                        <p className="text-error-main text-sm">
                          {errors.status}
                        </p>
                      )}
                    </div>
                    <div className="flex flex-col">
                      <label className="text-sm text-text-title">
                        Language *
                      </label>
                      <StyledSelect
                        options={Object.values(CourseLanguage).map(
                          (language) => ({
                            value: language,
                            label:
                              language.charAt(0).toUpperCase() +
                              language.slice(1),
                          })
                        )}
                        isClearable={false}
                        menuPosition="fixed"
                        value={{
                          value: values.language,
                          label:
                            values.language.charAt(0).toUpperCase() +
                            values.language.slice(1),
                        }}
                        onChange={(
                          value:
                            | SingleValue<OptionValue>
                            | MultiValue<OptionValue>
                        ) => {
                          if (value && isSelectSingleValue(value)) {
                            setValues({
                              ...values,
                              language: value.value as CourseLanguage,
                            });
                          }
                        }}
                      />
                      {errors.language && (
                        <p className="text-error-main text-sm">
                          {errors.language}
                        </p>
                      )}
                    </div>
                    <div className="flex flex-col">
                      <label className="text-sm text-text-title">Level *</label>
                      <StyledSelect
                        options={Object.values(CourseLevel).map((level) => ({
                          value: level,
                          label: level.charAt(0).toUpperCase() + level.slice(1),
                        }))}
                        isClearable={false}
                        menuPosition="fixed"
                        value={{
                          value: values.level,
                          label:
                            values.level.charAt(0).toUpperCase() +
                            values.level.slice(1),
                        }}
                        onChange={(
                          value:
                            | SingleValue<OptionValue>
                            | MultiValue<OptionValue>
                        ) => {
                          if (value && isSelectSingleValue(value)) {
                            setValues({
                              ...values,
                              level: value.value as CourseLevel,
                            });
                          }
                        }}
                      />
                      {errors.level && (
                        <p className="text-error-main text-sm">
                          {errors.level}
                        </p>
                      )}
                    </div>
                    <div className="flex flex-col bg-background-default p-4 rounded-lg shadow">
                      <label className="text-sm text-text-title">
                        Current Picture
                      </label>
                      {courseState.data?.pictureUrl ? (
                        <img
                          src={courseState.data.pictureUrl}
                          alt="Current course"
                          className="w-48 h-48 object-cover rounded-lg mt-2"
                        />
                      ) : (
                        <p className="text-sm text-text-title">
                          No image available
                        </p>
                      )}
                    </div>
                    <div className="flex flex-col bg-background-default p-4 rounded-lg shadow">
                      <label className="text-sm text-text-title">
                        Change Picture
                      </label>
                      <input
                        type="file"
                        id="picture"
                        accept="image/jpeg,image/png"
                        onChange={(event) =>
                          handleFileChange(event, setFieldValue)
                        }
                        className="border border-divider rounded-lg p-2"
                      />
                      {values.picture && (
                        <p className="text-sm text-text-title">
                          Selected: {values.picture.name}
                        </p>
                      )}
                      {errors.picture && (
                        <p className="text-error-main text-sm">
                          {errors.picture as string}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="space-y-4">
                    <div className="flex flex-col bg-background-default p-4 rounded-lg shadow">
                      <label className="text-sm text-text-title">
                        Authors *
                      </label>
                      <StyledSelect
                        multiple
                        isCloseMenuOnSelect={false}
                        loading={usersState.loading}
                        options={usersState.data.map((user) => ({
                          value: user.id,
                          label: `${user.name} - ${user.email}`,
                        }))}
                        menuPosition="fixed"
                        value={
                          values.authorIds
                            ? (values.authorIds
                                .map((authorId) => {
                                  const user = usersState.data.find(
                                    (u) => u.id === authorId
                                  );
                                  return user
                                    ? {
                                        value: user.id,
                                        label: `${user.name} - ${user.email}`,
                                      }
                                    : null;
                                })
                                .filter(Boolean) as OptionValue[])
                            : []
                        }
                        onChange={(
                          value:
                            | SingleValue<OptionValue>
                            | MultiValue<OptionValue>
                        ) => {
                          if (value && isSelectMultiValue(value)) {
                            setValues({
                              ...values,
                              authorIds: value.map((v) => v.value as string),
                            });
                          } else {
                            setValues({
                              ...values,
                              authorIds: [],
                            });
                          }
                        }}
                      />
                      {errors.authorIds && (
                        <p className="text-error-main text-sm">
                          {Array.isArray(errors.authorIds)
                            ? errors.authorIds.join(", ")
                            : errors.authorIds}
                        </p>
                      )}
                    </div>
                    <div className="flex flex-col bg-background-default p-4 rounded-lg shadow">
                      <label className="text-sm text-text-title">
                        Schedule *
                      </label>
                      {values.schedule
                        .sort(
                          (a, b) =>
                            new Date(a).getTime() - new Date(b).getTime()
                        )
                        .map((date) => (
                          <div className="flex flex-col" key={date}>
                            <div className="flex items-center space-x-2">
                              <div className="w-1/2 flex items-center justify-between border border-divider p-2 rounded-lg">
                                <label className="text-sm">
                                  {format(new Date(date), "PPp")}
                                </label>
                              </div>
                              <button
                                className="rounded-lg hover:bg-action-hover p-1 cursor-pointer"
                                onClick={() => {
                                  if (values.schedule.length > 1) {
                                    setValues({
                                      ...values,
                                      schedule: values.schedule.filter(
                                        (d) => d !== date
                                      ),
                                    });
                                  }
                                }}
                                disabled={values.schedule.length <= 1}
                                aria-label={
                                  values.schedule.length <= 1
                                    ? "At least one schedule date is required"
                                    : "Remove this schedule date"
                                }
                              >
                                <MinusOutlined
                                  className={`h-5 w-5 ${
                                    values.schedule.length > 1
                                      ? "text-error-main"
                                      : "text-action-disabled"
                                  }`}
                                />
                              </button>
                            </div>
                            <label className="text-xs text-text-title italic py-2">
                              and
                            </label>
                          </div>
                        ))}
                      <div className="flex items-center space-x-2">
                        <div className="w-1/2 flex items-center space-x-2">
                          <DatePicker
                            defaultValue={getDefaultScheduleDate(
                              values.schedule
                            )}
                            readOnly={false}
                            onChanged={(date) => setNewScheduleDate(date)}
                          />
                          {newScheduleDate && (
                            <TimePicker
                              value={newScheduleTime}
                              onChange={setNewScheduleTime}
                              disabled={false}
                            />
                          )}
                        </div>
                        <button
                          className="rounded-lg hover:bg-action-hover p-1 cursor-pointer"
                          onClick={() => {
                            if (newScheduleDate && newScheduleTime) {
                              const [hour, minute] = newScheduleTime.split(":");
                              if (
                                !hour ||
                                !minute ||
                                isNaN(parseInt(hour)) ||
                                isNaN(parseInt(minute))
                              ) {
                                toast("Invalid time format", { type: "error" });
                                return;
                              }
                              const isoDate = new Date(newScheduleDate);
                              isoDate.setHours(
                                parseInt(hour),
                                parseInt(minute),
                                0,
                                0
                              );
                              setValues({
                                ...values,
                                schedule: [
                                  ...values.schedule,
                                  isoDate.toISOString(),
                                ],
                              });
                              setNewScheduleDate(null);
                              setNewScheduleTime("");
                            }
                          }}
                          disabled={!(newScheduleDate && newScheduleTime)}
                          aria-label={
                            newScheduleDate && newScheduleTime
                              ? "Add schedule date"
                              : "Select a date and time to enable adding"
                          }
                        >
                          <AddOutlined
                            className={`w-5 h-5 ${
                              newScheduleDate && newScheduleTime
                                ? "text-success-main"
                                : "text-action-disabled"
                            }`}
                          />
                        </button>
                      </div>
                      {errors.schedule && (
                        <p className="text-error-main text-sm">
                          {errors.schedule as string}
                        </p>
                      )}
                    </div>
                    <div className="flex flex-col bg-background-default p-4 rounded-lg shadow">
                      <label className="text-sm text-text-title">
                        Attendance
                      </label>
                      {courseState.data?.registeredUsers.length ?? -1 > 0 ? (
                        <table className="w-full text-sm text-left text-text-title">
                          <thead>
                            <tr>
                              <th className="py-2 px-4 border-b">Name</th>
                              {values.schedule.map((date) => (
                                <th key={date} className="py-2 px-4 border-b">
                                  {format(new Date(date), "PP")}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {courseState.data?.registeredUsers.map((user) => (
                              <tr key={user.id}>
                                <td className="py-2 px-4 border-b">
                                  {user.name}
                                </td>
                                {values.schedule.map((date) => {
                                  const attendance = attendanceState.data.find(
                                    (att) =>
                                      att.user.id === user.id &&
                                      att.date === date
                                  );
                                  const localAtt = localAttendance.find(
                                    (att) =>
                                      att.userId === user.id &&
                                      att.date === date
                                  );
                                  return (
                                    <td
                                      key={date}
                                      className="py-2 px-4 border-b"
                                    >
                                      <CheckBox
                                        checked={
                                          localAtt
                                            ? localAtt.attended
                                            : attendance?.attended || false
                                        }
                                        onChange={(
                                          event: ChangeEvent<HTMLInputElement>
                                        ) =>
                                          handleLocalAttendanceChange(
                                            user.id,
                                            date,
                                            event.target.checked
                                          )
                                        }
                                      />
                                    </td>
                                  );
                                })}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      ) : (
                        <p>No registered users for this course.</p>
                      )}
                      <div className="mt-4">
                        <Button
                          label="Submit Attendances"
                          onClick={handleSubmitAttendance}
                          loading={
                            attendanceState.loading ||
                            markAttendanceState.loading ||
                            unmarkAttendanceState.loading
                          }
                          disabled={localAttendance.length === 0}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="flex items-center space-x-4 pt-4">
                  <Button
                    label="Save Changes"
                    type="submit"
                    loading={updateCourseState.loading}
                    disabled={
                      !dirty || isSubmitting || updateCourseState.loading
                    }
                  />
                </div>
              </form>
            )}
          </Formik>
        </>
      )}
      <ToastComponent id="updateCourse" />
    </PermissionGate>
  );
}
